import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import apiService from '@/services/api'
import { useAppDispatch, useAppSelector } from '@/store'
import { statusesReceived } from '@/store/slices/fileProcessingSlice'
import { FileItem, FileProcessingState } from '@/types'

/** Matches the poll cadence the smart rename/organize status hooks already use. */
const POLL_INTERVAL_MS = 3000

/**
 * Give up after roughly five minutes on one screen.
 *
 * A file whose queue worker died would otherwise be polled for as long as the tab stays
 * open. Stopping turns an invisible forever-loop into a badge the user can act on.
 */
const MAX_POLLS = 100

const SETTLED_STATES: FileProcessingState[] = ['ready', 'partial', 'failed']

const isSettled = (state?: FileProcessingState): boolean =>
  state !== undefined && SETTLED_STATES.includes(state)

/**
 * Keeps the processing state of the given files up to date.
 *
 * Polls only while something on screen is still being processed and stops the moment
 * everything settles, so a folder of finished documents costs nothing. Results go to the
 * store rather than being returned, so a tick only re-renders the rows that changed.
 */
export function useFileProcessingStatus(files: FileItem[]): { isStalled: boolean } {
  const dispatch = useAppDispatch()
  const overlay = useAppSelector((state) => state.fileProcessing.byId)
  const [isStalled, setIsStalled] = useState(false)

  const inFlightRef = useRef(false)
  const pollCountRef = useRef(0)

  const unsettledIds = useMemo(
    () =>
      files
        .filter((file) => !isSettled(overlay[file.id]?.processing_state ?? file.processing_state))
        .map((file) => file.id),
    [files, overlay]
  )

  // The array identity changes on every render; the effect below keys off this string so
  // it only restarts when the set of files actually being waited on changes.
  const unsettledKey = unsettledIds.join(',')

  const check = useCallback(async () => {
    // A slow response must not let ticks stack up on top of each other.
    if (inFlightRef.current) return

    const ids = unsettledKey ? unsettledKey.split(',').map(Number) : []
    if (ids.length === 0) return

    inFlightRef.current = true
    try {
      const statuses = await apiService.getFileStatuses(ids)
      if (statuses.length > 0) dispatch(statusesReceived(statuses))
    } catch (error) {
      console.error('Failed to refresh file processing status:', error)
    } finally {
      inFlightRef.current = false
    }
  }, [unsettledKey, dispatch])

  // Reset the budget whenever the set being watched changes — a new upload deserves its
  // own five minutes rather than inheriting the previous batch's spent ticks.
  useEffect(() => {
    pollCountRef.current = 0
    setIsStalled(false)
  }, [unsettledKey])

  useEffect(() => {
    if (!unsettledKey) return

    check()

    const interval = setInterval(() => {
      // Nothing is watching a background tab, and a forgotten one would otherwise poll
      // for as long as it stays open.
      if (document.visibilityState === 'hidden') return

      if (pollCountRef.current >= MAX_POLLS) {
        setIsStalled(true)
        clearInterval(interval)
        return
      }

      pollCountRef.current += 1
      check()
    }, POLL_INTERVAL_MS)

    // Coming back to the tab should show current state immediately, not after a tick.
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') check()
    }
    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisibilityChange)
    }
  }, [unsettledKey, check])

  return { isStalled }
}

export default useFileProcessingStatus
